import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { VoteEvolutionProject } from '@/types';

interface VoteEvolutionChartProps {
  projects: VoteEvolutionProject[];
  // Built server-side by VoteChart (app/lib/vote_chart.rb): one point per 30-minute
  // bucket, `{ timestamp, [projectId]: runningTotal }`.
  chartData: Array<Record<string, number>>;
  visibleStart: number;
}

const CHART_COLORS = [
  '#e1ff00',
  '#00ff87',
  '#ff006e',
  '#00d9ff',
  '#ffbe0b',
  '#ff7700',
  '#a100ff',
  '#00ffc8',
];

export function VoteEvolutionChart({
  projects,
  chartData,
  visibleStart,
}: VoteEvolutionChartProps) {
  const projectsMap = useMemo(() => {
    const map: Record<string, VoteEvolutionProject> = {};
    projects.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [projects]);

  const timeRange = useMemo(() => {
    if (chartData.length === 0)
      return { min: 0, max: 0, ticks: [] as number[] };

    const timestamps = chartData.map((d) => d.timestamp);
    const maxTime = Math.max(...timestamps);

    const visibleStartTime = visibleStart;

    const ticks: number[] = [];
    const eightHours = 8 * 60 * 60 * 1000;
    const startTick = Math.floor(visibleStartTime / eightHours) * eightHours;

    for (
      let tick = startTick;
      tick <= maxTime + eightHours;
      tick += eightHours
    ) {
      if (tick >= visibleStartTime) {
        ticks.push(tick);
      }
    }

    return { min: visibleStartTime, max: maxTime, ticks };
  }, [chartData, visibleStart]);

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${month} ${day}, ${hours}:${minutes}`;
  };

  return (
    <div className="w-full space-y-8">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {projects.map((project, index) => (
          <div
            key={project.id}
            className="flex items-center gap-3 rounded-lg border border-primary/20 bg-card p-4"
          >
            {project.logoUrl && (
              <img
                src={project.logoUrl}
                alt={project.name}
                width={40}
                height={40}
                className="rounded-md"
              />
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full"
                  style={{
                    backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                  }}
                />
                <p className="font-medium text-foreground text-sm">
                  {project.name}
                </p>
              </div>
              <p className="text-muted-foreground text-xs">
                {project.voteCount} votes
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-primary/20 bg-card p-6">
        <h3 className="mb-6 font-bold text-primary text-xl">
          Vote Accumulation Over Time
        </h3>
        <ResponsiveContainer width="100%" height={500}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={[timeRange.min, timeRange.max]}
              ticks={timeRange.ticks}
              tickFormatter={formatTime}
              stroke="hsl(var(--foreground))"
              tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke="hsl(var(--foreground))"
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                color: 'hsl(var(--foreground))',
              }}
              labelFormatter={(value) =>
                new Date(value as number).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              }
              formatter={(value, name) => [
                value as number,
                projectsMap[name as string]?.name || (name as string),
              ]}
            />
            <Legend
              wrapperStyle={{
                paddingTop: '20px',
                fontSize: '12px',
              }}
              formatter={(value) => projectsMap[value]?.name || value}
            />
            {projects.map((project, index) => (
              <Line
                key={project.id}
                type="monotoneX"
                dataKey={project.id}
                stroke={CHART_COLORS[index % CHART_COLORS.length]}
                strokeWidth={3}
                dot={false}
                name={project.name}
                animationDuration={300}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
